import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, Columns3, Gauge, Weight, Zap, Fuel, Rotate3d } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { emi, fromPrice, inr, media } from "@/lib/format";
import { useKiosk } from "./KioskContext";
import { OfferCard } from "./OfferCard";
import { EvSavingsTile, OnRoadTile } from "./PriceTiles";
import { Spinner } from "./Spinner";

const Stat = ({ label, value, unit, icon: Icon, testid, note }) => (
  <div className="tile flex min-w-0 flex-col justify-between p-6 [container-type:inline-size]" data-testid={testid}>
    <div className="flex items-start gap-2 text-[15px] font-medium leading-tight text-[#6E6E73]"><Icon size={17} className="mt-0.5 shrink-0" /> {label}</div>
    <div className="mt-4 whitespace-nowrap">
      <span className="font-display text-[min(56px,26cqw)] font-extrabold leading-none tracking-[-0.04em] tnum">{value}</span>
      <span className="ml-1 font-display text-[min(24px,11cqw)] font-bold text-[#6E6E73]">{unit}</span>
      {note && <p className="mt-1 text-[13px] text-[#6E6E73]">{note}</p>}
    </div>
  </div>
);

const ColourTile = ({ m, t, logEvent }) => {
  const [c, setC] = useState(0);
  const [spin, setSpin] = useState(false);
  const col = m.colours[c] || {};
  const canSpin = (col.spin || []).length > 4;
  return (
    <div className="tile relative col-span-12 flex flex-col overflow-hidden p-7 lg:col-span-7 lg:row-span-2 pk:!col-span-12 pk:!row-span-1" data-testid="colour-tile">
      <div className="flex items-center justify-between">
        <span className="text-[15px] font-medium text-[#6E6E73]">{t("colour")}</span>
        <div className="flex items-center gap-3">
          {canSpin && (
            <button onClick={() => { setSpin((s) => !s); logEvent("spin", m.slug); }} data-testid="spin-toggle"
              className={`flex min-h-[44px] items-center gap-1.5 rounded-full px-4 text-[14px] font-semibold ${spin ? "bg-[#1D1D1F] text-white" : "bg-white"}`}>
              <Rotate3d size={16} /> {t("spin")}
            </button>
          )}
          <span className="font-display text-xl font-bold tracking-[-0.02em]" data-testid="colour-name">{col.name}</span>
        </div>
      </div>
      <div className="relative flex min-h-[280px] flex-1 pk:!min-h-[30vh] items-center justify-center">
        <div className="floor-glow absolute bottom-4 left-1/2 h-20 w-[70%] -translate-x-1/2" />
        {spin && canSpin ? (
          <div className="relative w-full"><Spinner key={col.name} frames={col.spin} alt={`${m.name} 360`} /><p className="text-center text-[13px] text-[#6E6E73]">{t("dragToSpin")}</p></div>
        ) : (
        <AnimatePresence mode="wait">
          <motion.img key={col.image} src={media(col.image)} alt={`${m.name} in ${col.name}`} data-testid="colour-image"
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.35 }}
            className="product-shadow relative max-h-[340px] w-full object-contain pk:!max-h-[34vh]" />
        </AnimatePresence>
        )}
      </div>
      <div className="flex min-w-0 items-center gap-5">
        <span className="h-16 w-16 shrink-0 rounded-full shadow-inner ring-1 ring-black/15" style={{ background: col.hex }} data-testid="colour-big-swatch" />
        <div className="flex flex-wrap gap-3">
          {m.colours.map((x, i) => (
            <button key={x.name} onClick={() => setC(i)} aria-label={x.name} data-testid={`swatch-${i}`}
              className={`grid h-12 w-12 place-items-center rounded-full transition-transform active:scale-90 ${i === c ? "ring-2 ring-[#0B8577] ring-offset-4 ring-offset-[#F5F5F7]" : ""}`}>
              <span className="h-10 w-10 rounded-full ring-1 ring-black/20" style={{ background: x.hex }} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const FinanceTile = ({ price, settings, t, slug }) => {
  const [down, setDown] = useState(Math.round((price * settings.down_payment_default_pct) / 100 / 1000) * 1000);
  const [ti, setTi] = useState(Math.min(2, settings.tenures.length - 1));
  const months = settings.tenures[ti];
  const value = emi(price - down, settings.interest_rate, months);
  return (
    <div className="tile col-span-12 grid gap-6 p-7 lg:col-span-8 lg:grid-cols-[1fr_1.2fr] pk:!col-span-12" data-testid="finance-tile">
      <div className="flex flex-col justify-between">
        <span className="text-[15px] font-medium text-[#6E6E73]">{t("finance")}</span>
        <div>
          <span className="font-display text-[52px] font-extrabold leading-none tracking-[-0.04em] tnum" data-testid="emi-value">{inr(value)}</span>
          <span className="ml-1 text-lg text-[#6E6E73]">{t("perMonth")}</span>
          <p className="mt-2 text-[13px] text-[#6E6E73]" data-testid="emi-rate">{t("interest")} {settings.interest_rate}% p.a. · {t("indicative")}</p>
        </div>
        <Link to={`/kiosk/book/quote?model=${slug}`} className="btn-pill btn-teal mt-4 self-start" data-testid="detail-quote-btn">{t("getQuote")}</Link>
      </div>
      <div className="flex flex-col justify-center gap-8">
        <div>
          <div className="mb-4 flex justify-between text-[15px]"><span className="text-[#6E6E73]">{t("downPayment")}</span><span className="font-semibold tnum" data-testid="down-value">{inr(down)}</span></div>
          <Slider className="kiosk-slider" data-testid="down-slider" value={[down]} min={0} max={Math.round(price * 0.6 / 1000) * 1000} step={1000} onValueChange={(v) => setDown(v[0])} />
        </div>
        <div>
          <div className="mb-4 flex justify-between text-[15px]"><span className="text-[#6E6E73]">{t("tenure")}</span><span className="font-semibold tnum" data-testid="tenure-value">{months} {t("months")}</span></div>
          <Slider className="kiosk-slider" data-testid="tenure-slider" value={[ti]} min={0} max={settings.tenures.length - 1} step={1} onValueChange={(v) => setTi(v[0])} />
        </div>
      </div>
    </div>
  );
};

export default function ModelDetail() {
  const { slug } = useParams();
  const { data, t, tx, cat, setCompare, logEvent } = useKiosk();
  const navigate = useNavigate();
  const m = data.models.find((x) => x.slug === slug);
  const [vi, setVi] = useState(0);
  const variants = useMemo(() => m?.variants || [], [m]);
  useEffect(() => { logEvent("detail", slug); }, [slug, logEvent]);
  if (!m) return <div className="p-10"><Link to="/kiosk" className="btn-pill btn-ghost">{t("home")}</Link></div>;
  const v = variants[Math.min(vi, variants.length - 1)];
  const ev = m.powertrain === "EV";
  const s = m.specs;
  const addCompare = () => { logEvent("compare", slug); setCompare((c) => (c.includes(slug) ? c : [...c, slug].slice(-3))); navigate("/kiosk/compare"); };

  return (
    <main className="mx-auto max-w-[1280px] px-6 pb-12 lg:px-10" data-testid="model-detail">
      <div className="flex flex-wrap items-end justify-between gap-6 pt-4">
        <div>
          <Link to="/kiosk" className="inline-flex min-h-[44px] items-center gap-1 text-[16px] font-medium text-[#087568]" data-testid="detail-back"><ChevronLeft size={18} /> {t("home")}</Link>
          <p className="text-[17px] font-semibold text-[#087568]">{cat(m.category)}</p>
          <h1 className="font-display text-5xl lg:text-[72px] font-extrabold leading-none tracking-[-0.045em]" data-testid="detail-name">{m.name}</h1>
          <p className="mt-2 text-lg text-[#6E6E73]">{tx(m.tagline)}</p>
        </div>
        <div className="text-right">
          <p className="font-display text-4xl font-extrabold tracking-[-0.03em] tnum" data-testid="detail-price">{inr(v?.price)}</p>
          <p className="text-[14px] text-[#1D1D1F]">{t("exShowroom")} · {v?.name}</p>
          <p className="text-[13px] text-[#6E6E73]" data-testid="detail-price-asof">{t("indicative")} · {t("priceAsOn")} {m.price_as_on}</p>
          <div className="mt-3 flex justify-end gap-3">
            <button onClick={addCompare} className="btn-pill btn-ghost" data-testid="detail-compare-btn"><Columns3 size={18} /> {t("compare")}</button>
            <Link to={`/kiosk/book/test_ride?model=${m.slug}`} className="btn-pill btn-teal" data-testid="detail-book-btn">{t("bookTestRide")}</Link>
          </div>
        </div>
      </div>
      <div className="mt-8 grid grid-cols-12 gap-5">
        <ColourTile m={m} t={t} logEvent={logEvent} />
        <div className="tile col-span-12 flex min-w-0 flex-col justify-between p-7 [container-type:inline-size] lg:col-span-5 pk:!col-span-6" data-testid="engine-tile">
          <div className="flex items-center gap-2 text-[15px] font-medium text-[#6E6E73]">{ev ? <Zap size={17} /> : <Gauge size={17} />} {ev ? t("battery") : t("engine")}</div>
          <div className="mt-6">
            <p className="whitespace-nowrap"><span className="font-display text-[min(96px,25cqw)] font-extrabold leading-none tracking-[-0.05em] tnum">{ev ? s.battery_kwh : s.engine_cc}</span>
            <span className="ml-2 font-display text-[min(30px,9cqw)] font-bold text-[#6E6E73]">{ev ? "kWh" : "cc"}</span></p>
            <p className="mt-3 text-[16px] text-[#1D1D1F]">{[s.power, s.torque].filter(Boolean).join(" · ")}</p>
          </div>
        </div>
        <div className="col-span-12 grid min-w-0 grid-cols-2 gap-5 lg:col-span-5 pk:!col-span-6 pk:!grid-cols-1">
          <Stat testid="efficiency-tile" icon={ev ? Zap : Fuel} label={ev ? t("range") : t("mileage")} value={ev ? s.range_km : s.mileage_kmpl} unit={ev ? "km" : "km/l"} note={ev ? t("idcRange") : t("claimed")} />
          <Stat testid="kerb-tile" icon={Weight} label={t("kerbWeight")} value={s.kerb_kg} unit="kg" />
        </div>
        {v && <FinanceTile key={`${m.slug}-${v.name}`} price={v.price} settings={data.settings} t={t} slug={m.slug} />}
        <div className="tile col-span-12 p-7 lg:col-span-4 pk:!col-span-6" data-testid="variants-tile">
          <span className="text-[15px] font-medium text-[#6E6E73]">{t("variants")}</span>
          <div className="mt-3 flex flex-col gap-2">
            {variants.map((x, i) => (
              <button key={x.name} onClick={() => setVi(i)} data-testid={`variant-${i}`}
                className={`flex min-h-[48px] items-center justify-between rounded-2xl px-4 text-left text-[15px] transition-colors ${i === vi ? "bg-white shadow-sm" : "hover:bg-white/60"}`}>
                <span className="font-medium">{x.name}</span><span className="tnum">{inr(x.price)}</span>
              </button>
            ))}
          </div>
          <p className="mt-3 text-[12px] text-[#6E6E73]">{t("exShowroom")} · {ev ? t("subsidyNote") : t("onRoadNote")}</p>
        </div>
        {v && <OnRoadTile price={v.price} m={m} settings={data.settings} t={t} />}
        {ev ? <EvSavingsTile m={m} settings={data.settings} t={t} /> : <div className="col-span-12 lg:col-span-8 pk:!col-span-6"><OfferCard modelSlug={m.slug} inline /></div>}
        {ev && <div className="col-span-12"><OfferCard modelSlug={m.slug} inline /></div>}
      </div>
      <p className="mt-6 text-[12px] text-[#6E6E73]">{t("from")} {inr(fromPrice(m))} · {t("exShowroom")} · {t("indicative")}</p>
    </main>
  );
}
